import { ThemeEvents } from '@theme/events';

/**
 * Formats cents using a Shopify money format string.
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const value = Number(cents);
  if (!Number.isFinite(value)) return '';

  const formatString = format || '${{amount}}';

  /**
   * @param {number} number
   * @param {number} [precision]
   * @param {string} [thousands]
   * @param {string} [decimal]
   */
  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (Number.isNaN(number) || number == null) return '0';
    const fixed = (number / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  };

  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = formatString.match(placeholder);
  if (!match) return formatWithDelimiters(value);

  let formatted;
  switch (match[1]) {
    case 'amount':
      formatted = formatWithDelimiters(value, 2);
      break;
    case 'amount_no_decimals':
      formatted = formatWithDelimiters(value, 0);
      break;
    case 'amount_with_comma_separator':
      formatted = formatWithDelimiters(value, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      formatted = formatWithDelimiters(value, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      formatted = formatWithDelimiters(value, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      formatted = formatWithDelimiters(value, 0, ' ');
      break;
    case 'amount_with_space_separator':
      formatted = formatWithDelimiters(value, 2, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      formatted = formatWithDelimiters(value, 2, ' ', '.');
      break;
    default:
      formatted = formatWithDelimiters(value, 2);
  }

  return formatString.replace(placeholder, formatted);
}

/**
 * Persistent mobile purchase bar — always visible on PDP (≤749px).
 * Relocates quantity into the bar; price shows unit × quantity.
 */
class ProductStickyAtc extends HTMLElement {
  /** @type {MediaQueryList | null} */
  #media = null;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {HTMLElement | null} */
  #quantityEl = null;

  /** @type {HTMLElement | null} */
  #quantityHome = null;

  /** @type {number} */
  #unitPrice = 0;

  /** @type {number} */
  #compareAtPrice = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#unitPrice = Number(this.dataset.unitPrice) || 0;
    this.#compareAtPrice = Number(this.dataset.compareAtPrice) || 0;

    this.#media = window.matchMedia('(max-width: 749px)');
    this.#media.addEventListener('change', this.#onBreakpointChange, { signal });

    const section = this.closest('.shopify-section');
    this.#quantityEl =
      section?.querySelector('.buy-buttons-block quantity-selector-component, .buy-buttons-block .quantity-selector') ||
      null;
    this.#quantityHome = this.#quantityEl?.parentElement || null;

    this.#syncVisibility();
    this.#updateLinePrice();

    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    // Desktop qty stays in the form; listen on the section for input changes too
    section?.addEventListener('input', this.#onQuantityInput, { signal });
    section?.addEventListener('change', this.#onQuantityInput, { signal });
    this.addEventListener('input', this.#onQuantityInput, { signal });
    this.addEventListener('change', this.#onQuantityInput, { signal });
  }

  disconnectedCallback() {
    this.#restoreQuantity();
    this.#abort?.abort();
    this.#abort = null;
    document.body.classList.remove('has-sticky-atc');
  }

  #onBreakpointChange = () => {
    this.#syncVisibility();
  };

  #syncVisibility() {
    const isMobile = Boolean(this.#media?.matches);
    this.toggleAttribute('data-visible', isMobile);
    this.setAttribute('aria-hidden', isMobile ? 'false' : 'true');
    document.body.classList.toggle('has-sticky-atc', isMobile);

    if (isMobile) {
      this.#moveQuantityToBar();
      this.#updateLinePrice();
    } else {
      this.#restoreQuantity();
    }
  }

  #moveQuantityToBar() {
    const slot = this.querySelector('[data-sticky-qty-slot]');
    const qty = this.#quantityEl;
    if (!(slot instanceof HTMLElement) || !(qty instanceof HTMLElement)) return;
    if (qty.parentElement === slot) return;

    const formId = this.dataset.formId;
    const input = qty.querySelector('input[name="quantity"]');
    if (input instanceof HTMLInputElement && formId) {
      input.setAttribute('form', formId);
    }

    slot.appendChild(qty);
  }

  #restoreQuantity() {
    const qty = this.#quantityEl;
    const home = this.#quantityHome;
    if (!(qty instanceof HTMLElement) || !(home instanceof HTMLElement)) return;
    if (qty.parentElement === home) return;

    const input = qty.querySelector('input[name="quantity"]');
    if (input instanceof HTMLInputElement) {
      input.removeAttribute('form');
    }

    home.prepend(qty);
  }

  #getQuantity() {
    const input =
      this.querySelector('[data-sticky-qty-slot] input[name="quantity"]') ||
      this.#quantityEl?.querySelector('input[name="quantity"]') ||
      this.closest('.shopify-section')?.querySelector(
        '.buy-buttons-block input[name="quantity"], .product-form-buttons input[name="quantity"]'
      );

    if (!(input instanceof HTMLInputElement)) return 1;
    const value = Number.parseInt(input.value, 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  #updateLinePrice() {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const qty = this.#getQuantity();
    const linePrice = this.#unitPrice * qty;
    const lineCompare = this.#compareAtPrice > this.#unitPrice ? this.#compareAtPrice * qty : 0;
    const formattedPrice = formatMoney(linePrice, format);
    const formattedCompare = lineCompare > linePrice ? formatMoney(lineCompare, format) : '';

    const stickyCurrent = this.querySelector('[data-sticky-current]');
    const stickyCompare = this.querySelector('[data-sticky-compare]');

    if (stickyCurrent) {
      stickyCurrent.textContent = formattedPrice;
    }

    if (stickyCompare) {
      if (formattedCompare) {
        stickyCompare.hidden = false;
        stickyCompare.textContent = formattedCompare;
      } else {
        stickyCompare.hidden = true;
        stickyCompare.textContent = '';
      }
    }

    // Keep the main PDP price in sync with quantity (desktop + mobile header price)
    const section = this.closest('.shopify-section');
    const mainPrice = section?.querySelector('.product-details product-price .price, .product-details .price-block .price');
    const mainCompare = section?.querySelector(
      '.product-details product-price .compare-at-price, .product-details .price-block .compare-at-price'
    );

    if (mainPrice) {
      mainPrice.textContent = formattedPrice;
    }

    if (mainCompare instanceof HTMLElement) {
      if (formattedCompare) {
        mainCompare.hidden = false;
        mainCompare.textContent = formattedCompare;
      } else {
        mainCompare.hidden = true;
      }
    }
  }

  /** @param {Event} event */
  #onQuantityInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.name !== 'quantity') return;
    this.#updateLinePrice();
  };

  /** @param {CustomEvent} event */
  #onQuantityUpdate = () => {
    this.#updateLinePrice();
  };

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const detail = event.detail;
    if (!detail?.data) return;

    if (detail.data.newProduct) {
      this.dataset.productId = String(detail.data.newProduct.id);
    } else if (detail.data.productId && String(detail.data.productId) !== this.dataset.productId) {
      return;
    }

    const section = this.closest('.shopify-section');
    const nextQty =
      section?.querySelector('.buy-buttons-block quantity-selector-component, .buy-buttons-block .quantity-selector') ||
      this.querySelector('[data-sticky-qty-slot] quantity-selector-component, [data-sticky-qty-slot] .quantity-selector');

    if (nextQty instanceof HTMLElement && nextQty !== this.#quantityEl) {
      this.#quantityEl = nextQty;
      this.#quantityHome =
        section?.querySelector('.buy-buttons-block .product-form-buttons') || this.#quantityHome;
    }

    if (this.#media?.matches) {
      this.#moveQuantityToBar();
    }

    const resource = detail.resource;
    if (resource && typeof resource.price === 'number') {
      this.#unitPrice = resource.price;
      this.dataset.unitPrice = String(resource.price);

      const compare = typeof resource.compare_at_price === 'number' ? resource.compare_at_price : 0;
      this.#compareAtPrice = compare > resource.price ? compare : 0;
      this.dataset.compareAtPrice = String(this.#compareAtPrice);
    }

    this.#updateLinePrice();
    // product-price may morph after this handler — re-apply line total
    requestAnimationFrame(() => this.#updateLinePrice());

    const button = this.querySelector('[data-sticky-atc-button]');
    const label = this.querySelector('[data-sticky-atc-label]');
    const available = Boolean(resource?.available);

    if (button instanceof HTMLButtonElement) {
      button.disabled = !available;
    }

    if (label) {
      const sourceButton = detail.data.html?.querySelector?.('[ref="addToCartButton"]');
      const sourceText =
        sourceButton?.querySelector('.add-to-cart-text')?.textContent?.trim() ||
        sourceButton?.textContent?.trim();

      if (sourceText) {
        label.textContent = sourceText;
      }
    }
  };
}

if (!customElements.get('product-sticky-atc')) {
  customElements.define('product-sticky-atc', ProductStickyAtc);
}
